import { supabase } from './supabase';
import { clearUserContext } from './logger';

/**
 * The one admin sign-out path. Both callers — the sidebar Logout button in
 * `Layout` and the idle-timeout in `useAuth` — go through here, because they
 * previously carried two divergent copies of this sequence and only one of them
 * stopped auto-refresh.
 *
 * **What is deliberately NOT here: `queryClient.clear()`.**
 *
 * Clearing the cache removes every query, and any observer still MOUNTED at
 * that moment immediately re-creates its query and refetches. Clearing while
 * the admin's dashboard is still on screen therefore fires a fresh round of
 * reads at the exact instant the token is being revoked — and those reads reach
 * PostgREST as `anon`, which holds no grants on any application table. They come
 * back `42501 permission denied for table ...` a few seconds later, from a page
 * the admin has already left. That was six Sentry issues per logout.
 *
 * There is no ordering that fixes this: clear before the revoke and the refetch
 * races the token teardown; clear after it and the observers are still mounted
 * because the redirect has not committed yet. So the cache is instead cleared
 * when the NEXT session arrives (`useAuth`'s auth-state listener), which is
 * race-free — nothing is mounted to refetch, and the guarantee that actually
 * matters, that one admin never sees another's cached data, still holds.
 *
 * **Scope.** The Logout button signs out `global` (the default): an admin who
 * presses it means "sign me out", everywhere. The idle timeout passes `local`:
 * reception runs the same admin account on several devices, and an idle one
 * used to revoke every one of them — the device actually in use kept working on
 * its still-valid JWT until it hit an endpoint that checks the session
 * server-side (`/api/*` → 401), then the refresh failed and the admin was
 * thrown back to the login screen mid-task (Sentry 119210031, 2026-09-25).
 *
 * `local` is per BROWSER, not per tab: tabs share one stored session, so it
 * still ends every tab of this browser. `useAdminIdleTimeout` covers that case
 * by sharing its idle clock across tabs.
 */
export interface SignOutAdminOptions {
  scope?: 'global' | 'local';
}

export async function signOutAdmin({ scope = 'global' }: SignOutAdminOptions = {}): Promise<void> {
  // Stop attributing Sentry events to an admin who has left.
  clearUserContext();

  // Stop auto-refresh BEFORE revoking, or the SDK will try to rotate a refresh
  // token the server has already invalidated.
  supabase.auth.stopAutoRefresh();

  const { error } = await supabase.auth.signOut({ scope });
  if (error) throw error;
}
